import { Badge, Button, Dropdown, Layout, Menu } from 'antd';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AuditOutlined,
  BellOutlined,
  CalendarFilled,
  CalendarOutlined,
  DashboardFilled,
  DashboardOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  FormOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '../icons';
import { clearSession, getUser } from '../stores/session';
import { notificationsApi } from '../services/resources';
import { useEffect, useState } from 'react';
import type { Role } from '../types/api';

const FOOTER = '成都市第二十中学校 · 四川省成都市金牛区育英路6号 · 蜀ICP备15006431号-2';

const ADMIN = [
  { key: '/admin/home', label: '工作台', icon: <DashboardOutlined />, filled: <DashboardFilled /> },
  { key: '/admin/reservations', label: '预约审核', icon: <CalendarOutlined />, filled: <CalendarFilled /> },
  { key: '/admin/requests', label: '学生申请', icon: <FormOutlined />, filled: <FormOutlined /> },
  { key: '/admin/registrations', label: '注册审核', icon: <AuditOutlined />, filled: <AuditOutlined /> },
  { key: '/admin/labs', label: '实验室与节次', icon: <HomeOutlined />, filled: <HomeOutlined /> },
  { key: '/admin/courses', label: '开放实验', icon: <ExperimentOutlined />, filled: <ExperimentOutlined /> },
  { key: '/admin/showcases', label: '成果管理', icon: <TrophyOutlined />, filled: <TrophyOutlined /> },
  { key: '/admin/export', label: '数据导出', icon: <DownloadOutlined />, filled: <DownloadOutlined /> },
  { key: '/admin/migration', label: '迁移核对', icon: <TeamOutlined />, filled: <TeamOutlined /> },
];

const TEACHER = [
  { key: '/teacher/schedule', label: '我的课表', icon: <CalendarOutlined />, filled: <CalendarFilled /> },
  { key: '/teacher/reservations/new', label: '新建预约', icon: <FormOutlined />, filled: <FormOutlined /> },
  { key: '/teacher/requests/new', label: '代学生申请', icon: <FormOutlined />, filled: <FormOutlined /> },
];

const STUDENT = [
  { key: '/student/home', label: '首页', icon: <DashboardOutlined />, filled: <DashboardFilled /> },
  { key: '/student/requests/new', label: '申请', icon: <FormOutlined />, filled: <FormOutlined /> },
  { key: '/student/courses', label: '活动', icon: <ExperimentOutlined />, filled: <ExperimentOutlined /> },
  { key: '/student/showcases', label: '成果', icon: <TrophyOutlined />, filled: <TrophyOutlined /> },
];

function itemsFor(role: Role) {
  if (role === 'admin') return ADMIN;
  if (role === 'teacher') return TEACHER;
  return STUDENT;
}

export function AppShell() {
  const user = getUser();
  const loc = useLocation();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notes, setNotes] = useState<{ id: string; title?: string; body?: string }[]>([]);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const role = user?.role ?? 'student';
  const items = itemsFor(role);

  useEffect(() => {
    notificationsApi.unread().then((d) => setUnread(d.count ?? 0)).catch(() => setUnread(0));
  }, [loc.pathname]);

  const roleLabel = role === 'admin' ? '实验员' : role === 'teacher' ? '教师' : '学生';

  return (
    <Layout style={{ minHeight: '100dvh' }}>
      <Layout.Sider
        width={220}
        className="app-sider"
        breakpoint="lg"
        collapsedWidth={0}
        collapsed={menuCollapsed}
        onCollapse={setMenuCollapsed}
        onBreakpoint={setMenuCollapsed}
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[loc.pathname]}
          items={items.map((it) => ({
            key: it.key,
            icon: loc.pathname === it.key ? it.filled : it.icon,
            label: <Link to={it.key}>{it.label}</Link>,
          }))}
        />
      </Layout.Sider>
      <Layout>
        <Layout.Header className="row app-header" style={{ justifyContent: 'space-between' }}>
          <Link to="/" className="app-header-title" style={{ color: 'inherit' }}>
            <strong>大同科创共享中心预约平台</strong>
          </Link>
          <div className="row app-header-actions">
            <Button
              type="text"
              className="touch app-menu-btn"
              icon={<MenuOutlined />}
              onClick={() => setMenuCollapsed((v) => !v)}
              aria-label="打开导航菜单"
            >
              菜单
            </Button>
            <span className="muted app-header-user">
              {roleLabel}
              {user?.name ? ` ${user.name}` : ''}
            </span>
            <Dropdown
              trigger={['click']}
              popupRender={() => (
                <div className="surface" style={{ width: 320, padding: 12 }}>
                  {notes.length === 0 ? '暂无通知' : notes.map((n) => <p key={n.id}>{n.title ?? n.body}</p>)}
                </div>
              )}
            >
              <Button
                type="text"
                className="touch"
                aria-label="通知"
                onClick={() => {
                  notificationsApi.list().then((p) => setNotes(p.items)).catch(() => setNotes([]));
                }}
              >
                <Badge count={unread > 99 ? '99+' : unread} size="small">
                  <BellOutlined />
                </Badge>
              </Button>
            </Dropdown>
            <Button
              type="text"
              className="touch"
              icon={<HomeOutlined />}
              onClick={() => nav('/')}
              aria-label="返回主页"
            >
              返回主页
            </Button>
            <Button
              type="text"
              className="touch"
              icon={<LogoutOutlined />}
              onClick={() => {
                clearSession();
                nav('/login', { replace: true });
              }}
              aria-label="退出登录"
            >
              退出登录
            </Button>
          </div>
        </Layout.Header>
        <Layout.Content style={{ padding: 24, maxWidth: 1280 }}>
          <Outlet />
        </Layout.Content>
        <div className="app-footer">{FOOTER}</div>
        {role === 'student' ? (
          <nav className="mobile-tab">
            {STUDENT.map((it) => (
              <NavLink key={it.key} to={it.key} className={({ isActive }) => (isActive ? 'active' : '')}>
                {loc.pathname === it.key ? it.filled : it.icon}
                {it.label}
              </NavLink>
            ))}
            <NavLink to="/student/home" className="">
              <BellOutlined />
              我的
            </NavLink>
          </nav>
        ) : null}
        {role === 'teacher' ? (
          <nav className="mobile-tab" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {TEACHER.map((it) => (
              <NavLink key={it.key} to={it.key} className={({ isActive }) => (isActive ? 'active' : '')}>
                {loc.pathname === it.key ? it.filled : it.icon}
                {it.label === '我的课表' ? '课表' : '预约'}
              </NavLink>
            ))}
          </nav>
        ) : null}
      </Layout>
    </Layout>
  );
}
